/**
 * ALTIORA · TerritorySignalsWidget — Macro-pulse multi-région.
 *
 * Affiche les 3 signaux macro-économiques (chômage, emploi, population
 * active) pour Canada / Québec / Ontario, avec sélecteur segmenté.
 * Alimenté par `/api/territory/signals` (Statistique Canada WDS, cache 6h).
 */
import { useEffect, useMemo, useState } from "react";
import { TrendingUp, TrendingDown, Minus, Activity } from "lucide-react";
import { api } from "@/lib/api";
import {
  Card, Section, Loader,
  NAVY, CHAMPAGNE, GOLD, SLATE, IVORY,
} from "@/components/altiora/PremiumUI";

const GREEN = "#0B7A3B";
const RED   = "#B91C1C";
const REGION_ORDER = ["canada", "quebec", "ontario", "manitoba", "alberta", "british_columbia"];

// Labels courts pour le switcher (les longs comme "Colombie-Britannique" cassent l'UI)
const REGION_SHORT = {
  canada:            "Canada",
  quebec:            "Québec",
  ontario:           "Ontario",
  manitoba:          "Manitoba",
  alberta:           "Alberta",
  british_columbia:  "C.-B.",
};

function SignalIcon({ signal }) {
  if (signal === "positive") return <TrendingUp size={14} strokeWidth={2} style={{ color: GREEN }} />;
  if (signal === "negative") return <TrendingDown size={14} strokeWidth={2} style={{ color: RED }} />;
  return <Minus size={14} strokeWidth={2} style={{ color: SLATE }} />;
}

function fmtValue(v, unit) {
  if (v === null || v === undefined) return "—";
  const num = Number(v).toLocaleString("fr-CA", { maximumFractionDigits: 1 });
  return `${num}${unit === "%" ? " %" : unit === "k" ? " k" : ""}`;
}

function fmtDelta(d, unit) {
  if (d === null || d === undefined) return "";
  const sign = d > 0 ? "+" : "";
  const num = Number(d).toLocaleString("fr-CA", { maximumFractionDigits: 2 });
  return `${sign}${num}${unit === "%" ? " pt" : unit === "k" ? " k" : ""}`;
}

function fmtPeriod(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-CA", { year: "numeric", month: "short" });
  } catch { return iso; }
}

export default function TerritorySignalsWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [region, setRegion] = useState("canada");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/territory/signals");
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Indisponible");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const available = useMemo(() => {
    if (!data?.regions) return [];
    return REGION_ORDER.filter((k) => data.regions[k]);
  }, [data]);

  if (loading) return (
    <Card className="p-4" testid="territory-signals-loading">
      <div className="flex items-center gap-3 py-2">
        <Loader size={16} />
        <span className="text-[12px]" style={{ color: SLATE }}>Chargement des signaux territoire…</span>
      </div>
    </Card>
  );

  if (error || !data?.regions) return (
    <Card className="p-4" testid="territory-signals-error">
      <div className="text-[12px]" style={{ color: SLATE }}>
        Signaux territoire indisponibles pour le moment ({error || "aucune donnée"}).
      </div>
    </Card>
  );

  const active = data.regions[region] || data.regions.canada;
  const indicators = active.indicators;

  return (
    <Card className="p-5" testid="territory-signals-widget">
      <Section
        icon={Activity}
        title="Signaux territoire · Statistique Canada"
        hint={`Rafraîchi ${new Date(data.fetched_at).toLocaleString("fr-CA")} · désaisonnalisé.`}
      >
        {/* Segmented region switcher */}
        <div className="mt-2 mb-4 inline-flex rounded-md overflow-hidden"
             style={{ border: `1px solid ${CHAMPAGNE}44` }}
             data-testid="territory-region-switcher">
          {available.map((key) => {
            const on = key === region;
            return (
              <button
                key={key}
                onClick={() => setRegion(key)}
                data-testid={`territory-region-${key}`}
                aria-pressed={on}
                className="px-3 py-1.5 text-[11px] uppercase tracking-widest font-mono transition-colors"
                style={{
                  background: on ? NAVY : "transparent",
                  color: on ? IVORY : NAVY,
                  fontWeight: on ? 700 : 500,
                }}
              >
                {REGION_SHORT[key] || data.regions[key].label_fr}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {Object.entries(indicators).map(([key, ind]) => {
            const positive = ind.signal === "positive";
            const negative = ind.signal === "negative";
            const accent = positive ? GREEN : negative ? RED : SLATE;
            return (
              <div
                key={`${region}-${key}`}
                data-testid={`territory-signal-${region}-${key}`}
                className="rounded-md p-3 border"
                style={{ borderColor: `${CHAMPAGNE}44`, background: IVORY }}
              >
                <div className="text-[10px] font-mono uppercase tracking-[0.22em]"
                     style={{ color: CHAMPAGNE }}>
                  {ind.label_fr}
                </div>
                <div className="mt-1 flex items-baseline gap-2">
                  <div className="font-serif text-2xl tracking-tight" style={{ color: NAVY }}>
                    {fmtValue(ind.latest, ind.unit)}
                  </div>
                </div>
                <div className="mt-1 flex items-center gap-1.5 text-[11px]" style={{ color: accent }}>
                  <SignalIcon signal={ind.signal} />
                  <span>{fmtDelta(ind.delta, ind.unit)}</span>
                  <span className="text-[10px]" style={{ color: SLATE }}>
                    · vs {fmtPeriod(ind.prev_period)}
                  </span>
                </div>
                <div className="mt-0.5 text-[10px]" style={{ color: SLATE }}>
                  Période {fmtPeriod(ind.period)}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 text-[10px] font-mono uppercase tracking-[0.18em]"
             style={{ color: SLATE }}>
          {active.label_fr} · Labour Force Survey · <a href={data.source_url} target="_blank" rel="noopener noreferrer"
                              style={{ color: GOLD, textDecoration: "underline" }}>
            Web Data Service
          </a>
        </div>
      </Section>
    </Card>
  );
}
